/**
 * Triangle SOH-CAH-TOA — calculs purs.
 *
 * Triangle ABC rectangle en C : A à l'origine, [AC] horizontal, [CB]
 * vertical. On règle l'angle Â (en degrés) et l'hypoténuse AB. Vu depuis Â,
 * le côté opposé est [BC] et l'adjacent [AC] ; vu depuis B̂, c'est l'inverse.
 * Les rapports ne dépendent que de l'angle : exacts à 30°, 45° et 60°.
 */

import type { Pt } from '../../ui/frame';
import { exactValues, type ExactValue } from '../trig-circle/trig-values';

export type RefVertex = 'A' | 'B';
export type Role = 'opp' | 'adj' | 'hyp';
export type SideName = 'AB' | 'AC' | 'BC';

export const ANGLE_MIN = 5;
export const ANGLE_MAX = 85;
export const HYP_MIN = 2;
export const HYP_MAX = 9;

export interface RightTriangle {
  /** angle Â en degrés */
  angleA: number;
  /** hypoténuse AB */
  hyp: number;
}

export function vertices(t: RightTriangle): Record<'A' | 'B' | 'C', Pt> {
  const a = (t.angleA * Math.PI) / 180;
  const ac = t.hyp * Math.cos(a);
  const bc = t.hyp * Math.sin(a);
  return { A: { x: 0, y: 0 }, C: { x: ac, y: 0 }, B: { x: ac, y: bc } };
}

export function lengths(t: RightTriangle): Record<SideName, number> {
  const a = (t.angleA * Math.PI) / 180;
  return { AB: t.hyp, AC: t.hyp * Math.cos(a), BC: t.hyp * Math.sin(a) };
}

/** Le côté qui joue ce rôle pour l'angle choisi. */
export function sideFor(ref: RefVertex, role: Role): SideName {
  if (role === 'hyp') return 'AB';
  if (role === 'opp') return ref === 'A' ? 'BC' : 'AC';
  return ref === 'A' ? 'AC' : 'BC';
}

export function roleOf(ref: RefVertex, side: SideName): Role {
  return (['opp', 'adj', 'hyp'] as const).find((r) => sideFor(ref, r) === side)!;
}

export const ROLE_NAMES: Record<Role, string> = {
  opp: 'côté opposé',
  adj: 'côté adjacent',
  hyp: 'hypoténuse',
};

export function refAngle(t: RightTriangle, ref: RefVertex): number {
  return ref === 'A' ? t.angleA : 90 - t.angleA;
}

export type RatioName = 'cos' | 'sin' | 'tan';

export interface RatioReading {
  name: RatioName;
  /** « CAH », « SOH », « TOA » */
  mnemonic: string;
  num: SideName;
  den: SideName;
  numRole: Role;
  denRole: Role;
  value: number;
  /** valeur exacte aux angles remarquables (30°, 45°, 60°) */
  exact: ExactValue | null;
}

const RATIOS: ReadonlyArray<{ name: RatioName; mnemonic: string; num: Role; den: Role }> = [
  { name: 'cos', mnemonic: 'CAH', num: 'adj', den: 'hyp' },
  { name: 'sin', mnemonic: 'SOH', num: 'opp', den: 'hyp' },
  { name: 'tan', mnemonic: 'TOA', num: 'opp', den: 'adj' },
];

export function ratios(t: RightTriangle, ref: RefVertex): RatioReading[] {
  const len = lengths(t);
  const deg = refAngle(t, ref);
  const ex = Number.isInteger(deg) && deg % 15 === 0 ? exactValues(deg / 15) : null;
  return RATIOS.map((r) => {
    const num = sideFor(ref, r.num);
    const den = sideFor(ref, r.den);
    return {
      name: r.name,
      mnemonic: r.mnemonic,
      num,
      den,
      numRole: r.num,
      denRole: r.den,
      value: len[num] / len[den],
      exact: ex ? (r.name === 'cos' ? ex.cos : r.name === 'sin' ? ex.sin : ex.tan) : null,
    };
  });
}

/** Angle Â et hypoténuse pour un sommet B posé au doigt (angle entier, AB au demi). */
export function fromPoint(p: Pt): RightTriangle | null {
  if (p.x <= 0.2 || p.y <= 0.2) return null;
  const angleA = Math.round((Math.atan2(p.y, p.x) * 180) / Math.PI);
  const hyp = Math.round(Math.hypot(p.x, p.y) * 2) / 2;
  return {
    angleA: Math.min(ANGLE_MAX, Math.max(ANGLE_MIN, angleA)),
    hyp: Math.min(HYP_MAX, Math.max(HYP_MIN, hyp)),
  };
}

/** Défi : « quel est le côté adjacent à B̂ ? » */
export interface SideQuestion {
  ref: RefVertex;
  role: Role;
}

export function pickQuestion(rng: () => number, previous?: SideQuestion): SideQuestion {
  const all: SideQuestion[] = [];
  for (const ref of ['A', 'B'] as const)
    for (const role of ['opp', 'adj', 'hyp'] as const) all.push({ ref, role });
  const pool = previous
    ? all.filter((q) => q.ref !== previous.ref || q.role !== previous.role)
    : all;
  return pool[Math.min(pool.length - 1, Math.floor(rng() * pool.length))]!;
}

export const QUIZ_ROUNDS = 6;
